import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Pusher from 'pusher-js';

import LoadingIndicator from '../../global/loading_indicator/loading_indicator';
import axios from '../../global/axios';
import * as userActions from '../../global/user/actions/index';
import * as slidesActions from '../actions/index';

import SlideThumbnails from '../slide_thumbnails/slide_thumbnails';
import SlideCanvas from '../slide_canvas/slide_canvas';
import ElementAttributes from '../element_attributes/element_attributes';

class EditorView extends Component {

    constructor(props) {
        super(props);
        this.state = {
            collaborators: [],
            remoteCursors: {}
        };
        this.pusher = null;
        this.channel = null;
        this.saveTimeout = null;
        this.latestPresentation = null;
        this.lastOwnSaveTime = 0;
    }

    componentDidMount() {
        const docId = this.props.match.params.id;
        this.props.onFetchDocument(docId);
        this.initPusher(docId);
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.match.params.id;
        const newId = this.props.match.params.id;
        if (prevId !== newId) {
            this.props.onFetchDocument(newId);
            this.cleanupPusher();
            this.initPusher(newId);
        }
        // Clear stale sync cache once React has re-rendered with updated Redux props
        if (this.latestPresentation && this.props.current_document !== prevProps.current_document) {
            this.latestPresentation = null;
        }
    }

    componentWillUnmount() {
        this.cleanupPusher();
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
    }

    initPusher(docId) {
        try {
            this.pusher = new Pusher(window.PUSHER_KEY || '', {
                cluster: window.PUSHER_CLUSTER || 'us2',
                encrypted: true,
                authEndpoint: '/slides/editor/pusher_auth'
            });

            this.channel = this.pusher.subscribe('presence-slide-editor-' + docId);

            this.channel.bind('pusher:subscription_succeeded', (members) => {
                let collabs = [];
                members.each((member) => {
                    if (member.id !== String(this.props.current_user_id)) {
                        collabs.push({ id: member.id, name: member.info.name });
                    }
                });
                this.setState({ collaborators: collabs });
            });

            this.channel.bind('pusher:member_added', (member) => {
                this.setState(prevState => ({
                    collaborators: [...prevState.collaborators, { id: member.id, name: member.info.name }]
                }));
            });

            this.channel.bind('pusher:member_removed', (member) => {
                this.setState(prevState => ({
                    collaborators: prevState.collaborators.filter(c => c.id !== member.id),
                    remoteCursors: (() => {
                        const cursors = { ...prevState.remoteCursors };
                        delete cursors[member.id];
                        return cursors;
                    })()
                }));
            });

            this.channel.bind('slide_document_updated', (data) => {
                // Skip re-fetch for our own saves — the Pusher user ID check
                // can't work because we don't have the real user ID on the client.
                // Instead, skip events that arrive within 3s of our last save.
                if (Date.now() - this.lastOwnSaveTime < 3000) return;
                this.props.onFetchDocument(docId);
            });

            this.channel.bind('client-cursor-moved', (data) => {
                this.setState(prevState => ({
                    remoteCursors: { ...prevState.remoteCursors, [data.user_id]: data }
                }));
            });
        } catch (e) {
            console.warn('Pusher init failed:', e);
        }
    }

    cleanupPusher() {
        if (this.channel) {
            this.channel.unbind_all();
            this.pusher.unsubscribe(this.channel.name);
        }
        if (this.pusher) {
            this.pusher.disconnect();
        }
        this.pusher = null;
        this.channel = null;
    }

    getPresentation() {
        if (!this.props.current_document || !this.props.current_document.presentation_json) {
            return null;
        }
        try {
            return JSON.parse(this.props.current_document.presentation_json);
        } catch (e) {
            return null;
        }
    }

    handlePresentationChange = (updatedPresentation, saveImmediately) => {
        this.latestPresentation = updatedPresentation;
        this.props.onUpdatePresentationLocal(updatedPresentation);
        if (saveImmediately) {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
                this.saveTimeout = null;
            }
            this.saveNow(updatedPresentation);
        } else {
            this.debounceSave(updatedPresentation);
        }
    }

    saveNow(presentation) {
        if (this.props.current_document) {
            this.lastOwnSaveTime = Date.now();
            this.props.onSaveDocument(
                this.props.current_document.id,
                JSON.stringify(presentation),
                this.props.current_document.name
            );
        }
    }

    debounceSave(presentation) {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        this.saveTimeout = setTimeout(() => {
            if (this.props.current_document) {
                this.lastOwnSaveTime = Date.now();
                this.props.onSaveDocument(
                    this.props.current_document.id,
                    JSON.stringify(presentation),
                    this.props.current_document.name
                );
            }
        }, 500);
    }

    handleNameChange = (newName) => {
        if (this.props.current_document) {
            this.props.onSaveDocument(
                this.props.current_document.id,
                null,
                newName
            );
        }
    }

    broadcastCursor = (slideIndex, elementIndex) => {
        if (this.channel) {
            try {
                this.channel.trigger('client-cursor-moved', {
                    user_id: this.props.current_user_id,
                    slide_index: slideIndex,
                    element_index: elementIndex
                });
            } catch (e) {
                // Client events may not be enabled
            }
        }
    }

    handleSlideSelect = (index) => {
        this.props.onSelectSlide(index);
        this.broadcastCursor(index, null);
    }

    handleElementSelect = (index) => {
        this.props.onSelectElement(index);
        this.broadcastCursor(this.props.selected_slide_index, index);
    }

    flushSaveBeforeDownload = (callback) => {
        const doc = this.props.current_document;
        if (!doc) {
            callback();
            return;
        }

        // Cancel any pending debounce timer
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
            this.saveTimeout = null;
        }

        // Always save current local state to DB before downloading,
        // regardless of whether a debounce was pending or a save is in-flight.
        const presentation = this.latestPresentation || this.getPresentation();
        if (presentation) {
            this.lastOwnSaveTime = Date.now();
            this.props.onSaveDocument(
                doc.id,
                JSON.stringify(presentation),
                doc.name
            ).then(callback).catch(function () {
                alert('Save failed — download canceled.');
            });
        } else {
            callback();
        }
    }

    handleAddBlankSlide = () => {
        const presentation = this.getPresentation();
        if (!presentation) return;
        const updated = JSON.parse(JSON.stringify(presentation));
        const cues = updated.cues || [];
        const insertAt = (this.props.selected_slide_index != null ? this.props.selected_slide_index : cues.length - 1) + 1;
        const blankCue = {
            uuid: { string: 'blank-' + Date.now() },
            name: '',
            actions: [],
        };
        cues.splice(insertAt, 0, blankCue);
        updated.cues = cues;
        this.handlePresentationChange(updated, true);
        this.props.onSelectSlide(insertAt);
    }

    handleDownload = () => {
        const doc = this.props.current_document;
        if (!doc) return;

        this.flushSaveBeforeDownload(() => {
            const downloadUrl = '/slides/editor/' + doc.id + '/download?t=' + Date.now();
            axios.get(downloadUrl, { responseType: 'blob' })
                .then(function (res) {
                    var blob = new Blob([res.data], { type: 'application/octet-stream' });
                    var url = URL.createObjectURL(blob);
                    var a = document.createElement('a');
                    a.href = url;
                    a.download = doc.name + '.proBundle';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }).catch(function (err) {
                    var msg = 'Download failed';
                    if (err.response && err.response.data) {
                        var reader = new FileReader();
                        reader.onload = function () {
                            try {
                                var parsed = JSON.parse(reader.result);
                                alert('Download failed: ' + (parsed.error || reader.result));
                            } catch (e) {
                                alert('Download failed: ' + reader.result);
                            }
                        };
                        reader.readAsText(err.response.data);
                    } else {
                        alert(msg + ': ' + err.message);
                    }
                });
        });
    }

    handleDownloadPro = () => {
        const doc = this.props.current_document;
        if (!doc) return;

        this.flushSaveBeforeDownload(() => {
            var url = '/slides/editor/' + doc.id + '/download_pro?t=' + Date.now();
            axios.get(url, { responseType: 'blob' })
                .then(function (res) {
                    var disposition = res.headers['content-disposition'] || '';
                    var match = disposition.match(/filename="?([^"]+)"?/);
                    var filename = match ? match[1] : doc.name + '-' + Date.now() + '.pro';
                    var blob = new Blob([res.data], { type: 'application/octet-stream' });
                    var blobUrl = URL.createObjectURL(blob);
                    var a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(blobUrl);
                }).catch(function (err) {
                    alert('Download failed: ' + err.message);
                });
        });
    }

    handleDownloadJson = () => {
        const doc = this.props.current_document;
        if (!doc) return;

        this.flushSaveBeforeDownload(() => {
            var url = '/slides/editor/' + doc.id + '/download_json?t=' + Date.now();
            axios.get(url, { responseType: 'blob' })
                .then(function (res) {
                    var blob = new Blob([res.data], { type: 'application/json' });
                    var blobUrl = URL.createObjectURL(blob);
                    var a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = doc.name + '.json';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(blobUrl);
                }).catch(function (err) {
                    alert('Download failed: ' + err.message);
                });
        });
    }

    render() {
        if (this.props.loading_document) {
            return (
                <div className="w-full h-full flex items-center justify-center">
                    <LoadingIndicator />
                </div>
            );
        }

        const presentation = this.getPresentation();
        if (!presentation) {
            return (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <p>Select a document to edit</p>
                </div>
            );
        }

        const cues = presentation.cues || [];
        const selectedCue = cues[this.props.selected_slide_index] || null;
        let selectedElement = null;
        if (selectedCue && this.props.selected_element_index !== null) {
            const allElements = [];
            (selectedCue.actions || []).forEach(action => {
                const baseSlide = action.slide && action.slide.presentation && action.slide.presentation.baseSlide;
                if (baseSlide && baseSlide.elements) {
                    baseSlide.elements.forEach(el => allElements.push(el));
                }
            });
            selectedElement = allElements[this.props.selected_element_index] || null;
        }

        return (
            <div className="flex flex-col" style={{ minHeight: 200, height: 'calc(100vh - 200px)' }}>
                {/* Top bar */}
                <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white">
                    <div className="flex items-center space-x-3">
                        <input
                            type="text"
                            className="text-lg font-medium text-gray-800 border-none focus:outline-hidden focus:ring-0 bg-transparent"
                            value={this.props.current_document.name}
                            onChange={(e) => this.handleNameChange(e.target.value)}
                        />
                        <button
                            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition whitespace-nowrap"
                            onClick={this.handleAddBlankSlide}
                        >
                            + Blank Slide
                        </button>
                    </div>
                    <div className="flex items-center space-x-3">
                        {this.state.collaborators.map(c => (
                            <span key={c.id} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                {c.name}
                            </span>
                        ))}
                        {this.props.saving_document && (
                            <span className="text-xs text-gray-400">Saving...</span>
                        )}
                        {this.props.save_error && (
                            <span className="text-xs text-red-500">Save failed</span>
                        )}
                        <button
                            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition"
                            onClick={this.handleDownloadPro}
                        >
                            Download .pro
                        </button>
                        <button
                            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition"
                            onClick={this.handleDownload}
                        >
                            Download .proBundle
                        </button>
                        <button
                            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition"
                            onClick={this.handleDownloadJson}
                        >
                            Download .json
                        </button>
                    </div>
                </div>

                {/* Editor area */}
                <div className="flex flex-1 min-h-0 overflow-hidden">
                    {/* Slide thumbnails */}
                    <div className="w-48 border-r border-gray-200 bg-gray-50 overflow-y-auto">
                        <SlideThumbnails
                            cues={cues}
                            selectedIndex={this.props.selected_slide_index}
                            onSelectSlide={this.handleSlideSelect}
                            presentation={presentation}
                            onPresentationChange={this.handlePresentationChange}
                            documentId={this.props.current_document && this.props.current_document.id}
                        />
                    </div>

                    {/* Canvas */}
                    <div className="flex-1 overflow-auto bg-gray-200 p-4">
                        <SlideCanvas
                            cue={selectedCue}
                            selectedElementIndex={this.props.selected_element_index}
                            onSelectElement={this.handleElementSelect}
                            remoteCursors={this.state.remoteCursors}
                            collaborators={this.state.collaborators}
                            presentation={presentation}
                            slideIndex={this.props.selected_slide_index}
                            onPresentationChange={this.handlePresentationChange}
                            documentId={this.props.current_document && this.props.current_document.id}
                        />
                    </div>

                    {/* Element attributes */}
                    {selectedElement && (
                        <div className="w-72 border-l border-gray-200 bg-white overflow-y-auto shrink-0">
                            <ElementAttributes
                                element={selectedElement}
                                elementIndex={this.props.selected_element_index}
                                presentation={presentation}
                                slideIndex={this.props.selected_slide_index}
                                onPresentationChange={this.handlePresentationChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loading_document: state.slides.loading_document,
        current_document: state.slides.current_document,
        saving_document: state.slides.saving_document,
        save_error: state.slides.save_error,
        selected_slide_index: state.slides.selected_slide_index,
        selected_element_index: state.slides.selected_element_index,
        current_user_id: 'current',
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onUpdateAuthToken: (callback) => dispatch(userActions.updateAuthToken(callback)),
        onFetchDocument: (id) => dispatch(slidesActions.fetchDocument(id)),
        onSaveDocument: (id, presentationJson, name) => dispatch(slidesActions.saveDocument(id, presentationJson, name)),
        onSelectSlide: (index) => dispatch(slidesActions.selectSlide(index)),
        onSelectElement: (index) => dispatch(slidesActions.selectElement(index)),
        onUpdatePresentationLocal: (presentation) => dispatch(slidesActions.updatePresentationLocal(presentation)),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditorView));
